import React, { FC, useState, useEffect } from 'react';
import { Modal, Spin } from 'antd';
import { CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import '../mediaPreviewModal.scss';

interface FullScreenImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  altText?: string;
}

export const FullScreenImageModal: FC<FullScreenImageModalProps> = ({
  isOpen,
  onClose,
  imageUrl,
  altText = 'Full screen view',
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadedUrl, setLoadedUrl] = useState('');

  useEffect(() => {
    if (isOpen && imageUrl !== loadedUrl) {
      setIsLoading(true);
    }
  }, [isOpen, imageUrl, loadedUrl]);

  const antIcon = <LoadingOutlined style={{ fontSize: 48, color: '#ffa100' }} spin />;

  return (
    <Modal
      className="media-preview-modal"
      rootClassName="media-preview-modal-root"
      open={isOpen}
      onCancel={onClose}
      footer={null}
      centered
      width="auto"
      style={{ maxWidth: '90vw', padding: 0 }}
      closable={true}
      closeIcon={
        <span
          style={{
            color: '#fff',
            fontSize: '15px',
            backgroundColor: 'rgba(15, 23, 42, 0.9)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
          }}
        >
          <CloseOutlined />
        </span>
      }
      styles={{
        mask: {
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(20, 22, 28, 0.72)',
        },
        body: {
          padding: 0,
          background: 'transparent',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      }}
    >
      {imageUrl && (
        <div
          style={{
            minHeight: isLoading ? '300px' : 'auto',
            minWidth: isLoading ? '300px' : 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            borderRadius: '16px',
            overflow: 'hidden',
            background: 'rgba(14, 16, 32, 0.82)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.6)',
          }}
        >
          {isLoading && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2,
              }}
            >
              <Spin indicator={antIcon} />
            </div>
          )}
          <img
            src={imageUrl}
            alt={altText}
            onLoad={() => {
              setIsLoading(false);
              setLoadedUrl(imageUrl);
            }}
            style={{
              maxWidth: '100%',
              maxHeight: '80vh',
              objectFit: 'contain',
              display: isLoading ? 'none' : 'block',
              margin: '0 auto',
              borderRadius: '16px',
            }}
          />
        </div>
      )}
    </Modal>
  );
};
